import { Box, Button } from "@vinomofo/components";

import { navigate } from "gatsby";
import useAuth from "../../hooks/use-auth";
import { useCart } from "../../hooks/use-cart";
import { useEffect } from "react";
import useOneClickCheckout from "../../hooks/use-one-click-checkout";
import { useState } from "react";
import { useLocation } from "@reach/router";
import { LocalCartStorage } from "../../contexts/cart/store";
import {
  BIG_COMMERCE_CART_ID_COOKIE,
  GUEST_CART_EMAIL_COOKIE,
} from "../../utils/constants";
import styled from "styled-components";

const StyledWrapper = styled(Box)`
  @media screen and (max-height: 600px) {
    position: sticky;
    bottom: 0;
    background-color: white;
  }
`;

const LoadingSpan = styled.span<{ loading: boolean }>`
  position: relative;
  display: inline-block;
  left: ${({ loading }) => (loading ? `-10px` : `0`)};
  &::after {
    content: "";
    position: absolute;
    right: -26px;
    top: -2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: conic-gradient(#0000 10%, #4d4d4d);
    -webkit-mask: radial-gradient(
      farthest-side,
      #0000 calc(100% - 3.8px),
      #000 0
    );
    animation: spinner-zp9dbg 1s infinite linear;
  }

  @keyframes spinner-zp9dbg {
    to {
      transform: rotate(1turn);
    }
  }
`;

export interface CartActionsProps {
  onCloseRequested?: () => void;
}

export const CartActions = ({
  onCloseRequested,
}: CartActionsProps): JSX.Element => {
  const {
    isLoading,
    isMerging,
    isRedirecting,
    handleRedirectToCheckout,
    cart,
  } = useCart();
  const { isLoading: isAuthenticating } = useAuth();
  const [cookieEmail, setCookieEmail] = useState(null);
  const {
    isProcessing,
    isBuying,
    isRedirecting: oneClickCheckoutRedirecting,
  } = useOneClickCheckout();
  const location = useLocation();
  const emailParam = new URLSearchParams(location.search).get("email");

  const [checkoutDisabled, setCheckoutDisabled] = useState(true);

  const getCookieEmail = async () => {
    const _cookieEmail = await LocalCartStorage(
      BIG_COMMERCE_CART_ID_COOKIE,
      GUEST_CART_EMAIL_COOKIE
    ).getGuestCartEmail();
    setCookieEmail(_cookieEmail);
  };

  useEffect(() => {
    getCookieEmail();
  }, []);

  useEffect(() => {
    setCheckoutDisabled(
      isLoading ||
        isMerging ||
        isAuthenticating ||
        isRedirecting ||
        isBuying ||
        isProcessing ||
        oneClickCheckoutRedirecting
    );
  }, [
    isLoading,
    isMerging,
    isAuthenticating,
    isRedirecting,
    isProcessing,
    isBuying,
    oneClickCheckoutRedirecting,
  ]);

  return (
    <StyledWrapper
      width="100%"
      padding={3}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
    >
      <Button
        variant="outline"
        width="150px"
        disabled={checkoutDisabled}
        onClick={() => {
          navigate("/cart/", {
            state: { path: location.pathname },
          });
          onCloseRequested();
        }}
      >
        View Cart
      </Button>

      <Button
        width="150px"
        variant="primary"
        disabled={checkoutDisabled || cart?.withSoldOutItem}
        onClick={async () => {
          if (cart?.customerEmail || emailParam || cookieEmail) {
            handleRedirectToCheckout({
              email: cart?.customerEmail || emailParam || cookieEmail,
            });
          } else {
            await handleRedirectToCheckout({});
            onCloseRequested();
          }
        }}
        data-testid="redirect-to-checkout"
      >
        {isRedirecting ? (
          <LoadingSpan loading={isRedirecting}>Checkout</LoadingSpan>
        ) : (
          "Checkout"
        )}
      </Button>
    </StyledWrapper>
  );
};
